<template>
  <el-dialog
    :visible="visible"
    :before-close="handleClose"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="!saving && !fetching"
    width="720px"
    class="campaign-memo-dialog"
  >
    <div slot="title" class="campaign-memo-dialog__title">
      <span class="campaign-memo-dialog__title-text" :title="campaignTitle">{{ campaignTitle }}</span>
    </div>

    <div v-loading="fetching" class="campaign-memo-dialog__body">
      <div class="campaign-memo-dialog__label-row">
        <span class="campaign-memo-dialog__label">メモ</span>
        <span class="campaign-memo-dialog__counter">{{ counterText }}</span>
      </div>

      <el-input
        ref="textareaRef"
        v-model="draft"
        type="textarea"
        :rows="18"
        resize="none"
        placeholder="内容を入力"
        class="campaign-memo-dialog__textarea"
      />

      <div v-if="errorMessage" class="campaign-memo-dialog__error">{{ errorMessage }}</div>
    </div>

    <div slot="footer" class="campaign-memo-dialog__footer">
      <el-button
        type="primary"
        :disabled="!canSave || saving || fetching"
        :loading="saving"
        :aria-busy="saving"
        @click="handleSave"
      >
        <i class="mdi mdi-content-save" />
        保存
      </el-button>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue'

import { TrackingService } from '@/client/services'
import { normalizeLineEndings } from '@/common/utils'

import { useCampaignMemoStore } from '../hooks/useCampaignMemoStore'
import { formatMemoCounter, getMemoErrorMessage, isMemoOverLimit } from '../utils/campaignMemoText'

const { proxy } = getCurrentInstance() as any

interface Emits {
  (e: 'close'): void
  (e: 'saved'): void
}

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  campaignId: {
    type: Number,
    default: null
  }
})
const emit = defineEmits<Emits>()

const store = useCampaignMemoStore()
const { memo, fetching, saving } = storeToRefs(store)

const draft = ref('')
const textareaRef = ref<{ focus: () => void } | null>(null)

const normalizedDraft = computed(() => normalizeLineEndings(draft.value))
const counterText = computed(() => formatMemoCounter(draft.value))
const errorMessage = computed(() => getMemoErrorMessage(draft.value))
const canSave = computed(() => !isMemoOverLimit(draft.value))
const campaignTitle = computed(() => (memo.value ? memo.value.campaign_title || '' : ''))

watch(
  () => props.visible,
  async open => {
    if (!open || props.campaignId == null) {
      return
    }

    draft.value = ''

    try {
      await store.fetchMemo(props.campaignId)
      draft.value = memo.value ? memo.value.memo || '' : ''
      await nextTick()
      if (textareaRef.value) {
        textareaRef.value.focus()
      }
    } catch {
      proxy.$notify({
        title: 'キャンペーンメモの取得に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger',
        duration: 5000
      })
      emit('close')
    }
  }
)

const handleSave = async (): Promise<void> => {
  if (!canSave.value || props.campaignId == null || saving.value) {
    return
  }
  TrackingService.sendEvent('click:キャンペーンメモ|保存')

  try {
    await store.saveMemo(props.campaignId, normalizedDraft.value)
    proxy.$notify({
      title: 'キャンペーンメモを保存しました。',
      customClass: 'success',
      duration: 5000
    })
    emit('saved')
  } catch {
    proxy.$notify({
      title: 'キャンペーンメモの保存に失敗しました。',
      message: '恐れ入りますが、時間をおいて再度お試しください。',
      customClass: 'danger',
      duration: 5000
    })
  }
}

const handleClose = (): void => {
  if (saving.value || fetching.value) {
    return
  }
  draft.value = ''
  emit('close')
}
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.campaign-memo-dialog {
  :deep(.el-dialog__header) {
    padding: $spacing-unit * 2;
    font-size: $font-large-size;
    color: $text-color-white !important;
    background-color: $background-dark-color;
  }

  :deep(.el-dialog__body) {
    padding: $spacing-unit * 2;
    padding-bottom: 0;
  }

  :deep(.el-dialog__footer) {
    padding: 0 $spacing-unit * 2 $spacing-unit * 2;
  }

  :deep(.el-dialog__title),
  :deep(.el-dialog__headerbtn .el-dialog__close) {
    color: $text-color-white;
  }

  &__title-text {
    display: inline-block;
    max-width: 600px;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
    white-space: nowrap;
  }

  &__label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $spacing-unit;
  }

  &__label {
    font-weight: bold;
  }

  &__counter {
    font-size: $font-small-size;
    color: $sub-text-color;
  }

  &__textarea {
    :deep(.el-textarea__inner) {
      resize: none;
    }
  }

  &__error {
    margin-top: $spacing-unit;
    color: $danger-color;
  }

  &__footer {
    display: flex;
    gap: $spacing-unit;
    justify-content: flex-end;
    margin-top: $spacing-unit;
  }
}
</style>
